import { useCallback, useMemo, useRef, useState } from 'react';

import type { NavItemConfigInternal } from '../../../types';

interface ExpandOpenState {
	isExpandable: boolean;
	isExpandOrFlyoutOpen: boolean;
	onExpandFlyoutToggle: (isExpandedOrOpen: boolean) => void;
}

/**
 * Allows managing expands and flyouts open state so they can collapse during drag.
 * The consuming apps may also want to control this state. So, we track the open state here and also listen for
 * the corresponding props so we can update our internal state if the app is controlling it.
 */
export function useExpandOpenState(item: NavItemConfigInternal, peek: boolean): ExpandOpenState {
	const { type } = item;

	const isExpandable = ['expand', 'flyout', 'object-list-flyout'].includes(type);
	const isExpandableOnPeek = ['expand', 'flyout'].includes(type);

	const isExpandedFromProps =
		(type === 'expand' ? item.isExpanded : undefined) ??
		(type === 'flyout' || type === 'object-list-flyout' ? item.isOpen : undefined);

	const isDefaultExpanded =
		(type === 'expand' ? item.isDefaultExpanded : undefined) ??
		(type === 'flyout' || type === 'object-list-flyout' ? item.isDefaultOpen : undefined);

	// Peeked expand/flyout items should open initially, even if they are controlled closed.
	const initState =
		isExpandableOnPeek && peek
			? true
			: typeof isExpandedFromProps === 'boolean'
				? isExpandedFromProps
				: typeof isDefaultExpanded === 'boolean'
					? isDefaultExpanded
					: false;

	const [isExpandOrFlyoutOpen, setIsExpandOrFlyoutOpen] = useState(initState);

	const hookParams = useMemo(
		() => ({
			isExpandedFromProps,
			isExpandable,
			peek,
			isExpandableOnPeek,
			setIsExpandOrFlyoutOpen,
		}),
		[isExpandedFromProps, isExpandable, peek, isExpandableOnPeek, setIsExpandOrFlyoutOpen],
	);

	useCustomSyncState(hookParams);

	const onExpanded =
		(type === 'expand' ? item.onExpansionToggle : undefined) ??
		(type === 'flyout' || type === 'object-list-flyout' ? item.onOpenChange : undefined);

	const onExpandFlyoutToggle = useCallback(
		(isExpandedOrOpen: boolean) => {
			if (!isExpandable) {
				return;
			}

			setIsExpandOrFlyoutOpen(isExpandedOrOpen);
			onExpanded?.(isExpandedOrOpen);
		},
		[isExpandable, onExpanded],
	);

	return {
		isExpandOrFlyoutOpen,
		onExpandFlyoutToggle,
		isExpandable,
	};
}

interface UseCustomSyncStateParams {
	isExpandable: boolean;
	isExpandableOnPeek: boolean;
	isExpandedFromProps?: boolean;
	peek: boolean;
	setIsExpandOrFlyoutOpen: (value: React.SetStateAction<boolean>) => void;
}

// Sync internal state when controlled props change during render (not in an effect).
// This avoids hydration mismatches caused by useEffect setting state post-hydration.
// See: https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
function useCustomSyncState(params: UseCustomSyncStateParams) {
	const { isExpandedFromProps, isExpandable, peek, isExpandableOnPeek, setIsExpandOrFlyoutOpen } =
		params;

	const prevIsExpandedFromProps = useRef(isExpandedFromProps);
	const prevPeek = useRef(peek);

	if (isExpandable) {
		if (isExpandedFromProps !== prevIsExpandedFromProps.current) {
			prevIsExpandedFromProps.current = isExpandedFromProps;
			if (typeof isExpandedFromProps === 'boolean' && !peek) {
				setIsExpandOrFlyoutOpen(isExpandedFromProps);
			}
		}
		if (peek !== prevPeek.current) {
			prevPeek.current = peek;
			if (isExpandableOnPeek && peek) {
				setIsExpandOrFlyoutOpen(true);
			}
		}
	}
}
